import { cva } from 'class-variance-authority';
import { useStepper } from '../../hooks';
import { StepItem } from '../molecules';
import { StepperProps } from '@/shared/types/stepper';
import { cn } from '@/shared/utils/cn';

/**
 * Variantes del Stepper usando tokens semánticos
 */
const stepperVariants = cva('', {
  variants: {
    color: {
      blue: '',
      red: '',
      green: '',
      orange: '',
      yellow: '',
      'blue-dark': '',
      'blue-light': '',
      neutral: '',
      white: '',
      black: '',
      transparent: '',
    },
    size: {
      xs: '',
      sm: '',
      md: '',
      lg: '',
    },
  },
  defaultVariants: {
    color: 'blue',
    size: 'md',
  },
});

/**
 * Stepper - Componente de pasos/progreso de pasos
 *
 * Componente que muestra un conjunto de pasos con indicadores de estado (completado, activo, pendiente).
 * Soporta orientación horizontal y vertical, múltiples colores y tamaños.
 *
 * @example
 * ```tsx
 * // Stepper horizontal
 * <Stepper
 *   steps={[
 *     { label: 'Paso 1', status: 'completed' },
 *     { label: 'Paso 2', status: 'active' },
 *     { label: 'Paso 3', status: 'pending' }
 *   ]}
 * />
 *
 * // Stepper vertical
 * <Stepper
 *   steps={steps}
 *   orientation="vertical"
 *   color="blue"
 * />
 * ```
 *
 * Tokens semánticos de Figma:
 * - Layout: flex, items-center
 * - Orientación: horizontal (default) o vertical
 * - Scroll opcional para muchos pasos
 *
 * @see {@link StepperProps} para la lista completa de props
 */
export const Stepper = ({
  steps,
  color,
  size,
  className,
  classNameStep,
  classNameStepLabel,
  classNameStepIcon,
  classNameStepNumber,
  currentStep: externalCurrentStep,
  onClickStep,
  onStepChange,
  withScroll = true,
  vertical = false,
  stepClickable = false,
  white = false,
}: StepperProps) => {
  const { currentStep, getColorClasses, handleStepClick, getStepStatus } = useStepper({
    steps,
    currentStep: externalCurrentStep,
    onClickStep,
    onStepChange,
  });

  const currentColor = getColorClasses(color || 'blue');

  // Clases del contenedor según orientación
  const containerClasses = !vertical
    ? [
      'ui-flex',
      'ui-items-center',
      'ui-min-w-0',
      'ui-relative',
      withScroll && 'ui-overflow-x-auto ui-overflow-y-hidden',
    ]
      .filter(Boolean)
      .join(' ')
    : [
      'ui-flex',
      'ui-flex-col',
      'ui-items-start',
      'ui-min-h-0',
      'ui-relative',
      withScroll && 'ui-overflow-y-auto ui-overflow-x-hidden',
    ]
      .filter(Boolean)
      .join(' ');

  return (
    <div className={cn('ui-imj', containerClasses, stepperVariants({ color, size, className }))}>
      {steps.map((step, index) => {
        const status = getStepStatus(index);
        const isLast = index === steps.length - 1;

        return (
          <StepItem
            key={index}
            step={step}
            stepIndex={index}
            status={status}
            colorClasses={currentColor}
            isLast={isLast}
            onStepClick={handleStepClick}
            classNameStep={classNameStep}
            classNameStepLabel={classNameStepLabel}
            classNameStepIcon={classNameStepIcon}
            classNameStepNumber={classNameStepNumber}
            vertical={vertical}
            stepClickable={stepClickable}
            white={white}
          />
        );
      })}
    </div>
  );
};
